/**
 * State and save orchestration for the reassign-order modal.
 * @module reAssignModal/hooks/useReAssignModal
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { closeModal } from "risa-oasis-ui_v2";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { RootState } from "../../../../redux/store/store";
import { logError } from "../../../../utils/customLogger";
import { getAssigneeDisplayName } from "../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { ToastMessages } from "../../../toasts/toastMessages";
import {
  OrderReassignmentPayload,
  ProviderSelectOption,
  ReAssignModalProps,
  ReassignModalProvider,
} from "../types";
import { buildOrderReassignmentPayload } from "../utils/buildOrderReassignmentPayload";
import { buildProviderSelectOptions } from "../utils/buildProviderSelectOptions";
import { insertReassignTrailComments } from "../utils/insertReassignTrailComments";
import { logReassignSaveAnalytics } from "../utils/logReassignSaveAnalytics";

/** Return value of {@link useReAssignModal}. */
export interface UseReAssignModalResult {
  /** Selected provider id awaiting save. */
  providerId: string;
  /** Updates the selected provider id from the searchable select. */
  setProviderId: (providerId: string) => void;
  /** Sorted provider options including the unassigned entry. */
  options: ProviderSelectOption[];
  /** True while a single-order or bulk Postgres update is in flight. */
  loading: boolean;
  /** Persists assignment via single or bulk update, then closes the modal. */
  assignOrderToProvider: () => Promise<void>;
  /** Aborts any in-flight single-order or bulk update request. */
  abortAll: () => void;
  /** Oasis dialog id passed to {@link closeModal} on save. */
  dialogId: string;
}

/**
 * Owns reassignment state, API hooks, and the save handler for {@link ReAssignModal}.
 *
 * Chooses bulk Postgres update when linked siblings exist in row metadata;
 * otherwise uses a single-order update. Trail comments and analytics follow
 * the same parent-vs-siblings split.
 *
 * @param props - {@link ReAssignModalProps} from the modal container
 * @returns View state and handlers for the modal shell and body
 */
export const useReAssignModal = (
  props: ReAssignModalProps,
): UseReAssignModalResult => {
  const [providerId, setProviderId] = useState("");
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { data: providersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { runUpdate, abort, loading } =
    useUpdateOrderDetails<OrderReassignmentPayload>({
      orderId: props.id,
      orgId,
      tableName: SingleOrderDetailsTableName.ASSIGNEE,
    });

  const providerList = providersDetails as ReassignModalProvider[] | undefined;
  const options = useMemo(
    () => buildProviderSelectOptions(providerList),
    [providerList],
  );
  const dialogId = `reAssign-modal-${props.id}`;

  const abortAll = () => {
    abort();
  };

  const assignOrderToProvider = async () => {
    if (props.shouldLogEvent) {
      logReassignSaveAnalytics({
        orderId: props.id,
        patientId: (props.metaData?.patient_id as string) ?? "",
        orgId: orgId ?? "",
        userId: user?.id ?? "",
        userEmail: user?.email ?? "",
        assignedToId: props.assignedToId,
        newProviderId: providerId,
        providersDetails: providerList,
      });
    }

    const payload = buildOrderReassignmentPayload(providerId, providerList);
    const result = await runUpdate(payload);

    if (result.success) {
      const fromAssignee = getAssigneeDisplayName(
        props.assignedToId,
        props.assignedTo,
      );
      const toAssignee = getAssigneeDisplayName(
        providerId,
        payload.assigned_to_name,
      );
      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";

      insertReassignTrailComments({
        orderIds: result.data?.orders_updated ?? [props.id],
        orgId: orgId ?? "",
        patientId: (props.metaData?.patient_id as string) ?? undefined,
        providerEmail: user?.email ?? "",
        providerName: actingProviderName,
        fromAssignee,
        toAssignee,
      });

      toast.success(ToastMessages.ASSIGN_ORDER_SUCCESS);
      props.onComplete?.();
    } else {
      logError(
        result.error as Error,
        "Error while assigning order to provider",
      );
      toast.error(ToastMessages.ASSIGN_ORDER_FAILED);
    }

    closeModal(dialogId);
  };

  return {
    providerId,
    setProviderId,
    options,
    loading: loading,
    assignOrderToProvider,
    abortAll,
    dialogId,
  };
};
